/**
 * SimpleModel class for managing data with configurable accessors.
 */
class SimpleModel {
    /**
     * Constructs a new SimpleModel instance.
     * @param {Object} data - The initial data object.
     */
    constructor(data) {
        this.data = data;
    }

    /**
     * Configures accessors for attributes based on the provided method name and list.
     * @param {string} methodName - The method to call for each attribute.
     * @param {Object} list - The list of attributes and their types.
     */
    __configureAccessors(methodName, list) {
        for (let attribute in list) {
            if (list.hasOwnProperty(attribute)) {
                const objectType = list[attribute];
                this[methodName](attribute, objectType);
            }
        }
    }

    /**
     * Creates a writable accessor for the given attribute.
     * @param {string} attribute - The attribute name.
     * @param {Object} objectType - The type of the attribute.
     */
    writeable(attribute, objectType) {
        const prefix = objectType === Boolean ? 'setIs' : 'set';
        const funcName = prefix + attribute.charAt(0).toUpperCase() + attribute.slice(1);

        this[funcName] = function(value) {
            this.data[attribute] = value;
        }.bind(this);
    }

    /**
     * Dumps the current data to the console.
     */
    dump() {
        if (console && console.log) {
            console.log('this.data', this.data);
        }
    }

    /**
     * Creates a readable accessor for the given attribute.
     * @param {string} attribute - The attribute name.
     * @param {Object} objectType - The type of the attribute.
     */
    readable(attribute, objectType) {
        const prefix = objectType === Boolean ? 'is' : 'get';
        const funcName = prefix + attribute.charAt(0).toUpperCase() + attribute.slice(1);

        this[funcName] = function() {
            return this.data[attribute];
        }.bind(this);
    }

    /**
     * Creates both readable and writable accessors for the given attribute.
     * @param {string} attribute - The attribute name.
     * @param {Object} objectType - The type of the attribute.
     */
    accessable(attribute, objectType) {
        this.writeable(attribute, objectType);
        this.readable(attribute, objectType);
    }

    /**
     * Configures readable accessors for a list of attributes.
     * @param {Object} readables - The list of attributes and their types.
     */
    readables(readables) {
        this.__configureAccessors('readable', readables);
    }

    /**
     * Configures writable accessors for a list of attributes.
     * @param {Object} writeables - The list of attributes and their types.
     */
    writables(writeables) {
        this.__configureAccessors('writeable', writeables);
    }

    /**
     * Configures both readable and writable accessors for a list of attributes.
     * @param {Object} accessables - The list of attributes and their types.
     */
    accessables(accessables) {
        this.__configureAccessors('accessable', accessables);
    }
}

export default SimpleModel;